import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import { useProfilesStore } from '@/stores/profiles';
import { getUniqueInstitutions } from '@/utils/helpers';
import api from '@/api';
import { GalleryEntryFragment, GalleryFragment } from '@/graphql/custom-types';
import { IInstitution } from '@/interfaces';
import { ScreenProfile } from '@/graphql/generated-types';

interface IGalleriesState {
  all: GalleryFragment[];
  allCopy: GalleryFragment[];
  galleryEntry: GalleryEntryFragment | Record<string, never>;
  categories: string[];
  selectedCategory: string;
  uniqueInstitutions: IInstitution[];
}

export const useGalleriesStore = defineStore('galleries', {
  state: (): IGalleriesState => ({
    all: [],
    allCopy: [],
    galleryEntry: {},
    categories: [],
    selectedCategory: 'AllCategoriesSelected',
    uniqueInstitutions: [],
  }),
  actions: {
    async getGalleries() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      try {
        const galleries = await api.gallery.getGalleries(departmentStore.id);
        this.all = galleries;
        this.allCopy = galleries;
        this.uniqueInstitutions = getUniqueInstitutions(galleries);
      } catch (error) {
        console.log('Error getting galleries', error);
      }
    },
    async resetGalleryEntry() {
      this.galleryEntry = {};
    },
    async getGalleryEntry(id: string) {
      try {
        this.galleryEntry = await api.gallery.getGalleryEntry(id);
      } catch (error) {
        console.log('Error getting gallery', error);
      }
    },
    async archiveAddScreen({
      resourceId,
      profile,
      participating,
    }: {
      resourceId: string;
      profile: ScreenProfile;
      participating: boolean;
    }) {
      const profilesStore = useProfilesStore();

      try {
        if (participating) {
          await api.archive.archiveAdd(profile, resourceId);
        } else {
          await api.archive.archiveRemove(profile, resourceId);
        }

        profilesStore.setPinValidation({
          validating: false,
          validPin: true,
        });

        profilesStore.getMyArchive({
          id: profile.profileId,
          type: profile.profileType,
        });
      } catch {
        profilesStore.setPinValidation({
          validating: false,
          validPin: false,
        });
        throw new Error('Could not update archive');
      }
    },
    setCategories(categories: string[]) {
      this.categories = categories;
    },
    setSelectedCategory(category: string) {
      this.selectedCategory = category;
    },
  },
});
